import { useAppDispatch } from '@/hooks'
import { referralsCollection } from '@/reducers/referrals'
import { Referral } from '@/types/referral'
import { Transaction } from '@/types/transaction'
import { fetchAuthenticated } from '@/utils/fetch'
import { useEffect, useMemo, useState } from 'react'
import { ViewModelProps } from '../../rewards.viewmodel'
import { getProgramNamesWithExternalPayouts } from './Activity.utils'
import {
  EXPIRING_BANNER_WINDOW,
  EXPIRING_SOON_CARD_WINDOW,
  getExpiringSoonSchedule,
} from '../../rewards.utils'

export type ActivityViewModelProps = ReturnType<typeof useActivityViewModel>

export function useActivityViewModel(parentProps: ViewModelProps) {
  const dispatch = useAppDispatch()

  const [reminderModalOpen, setReminderModalOpen] = useState(false)
  const [loadedTransactions, setLoadedTransactions] = useState(5)
  const [transactionToDisplay, setTransactionToDisplay] =
    useState<Transaction | null>(null)

  const programNamesWithExternalPayouts = getProgramNamesWithExternalPayouts(
    parentProps.programOverviews,
  )

  // Shown whenever there's a schedule, even before the backend starts
  // expiring points, so customers see what's coming
  const expirationSchedule = parentProps.transactions?.[0]?.expiration_schedule
  const nextExpiration = useMemo(
    () =>
      getExpiringSoonSchedule(
        expirationSchedule,
        new Date(),
        EXPIRING_BANNER_WINDOW,
      )[0],
    [expirationSchedule],
  )
  const expiringSoonSchedule = useMemo(
    () =>
      getExpiringSoonSchedule(
        expirationSchedule,
        new Date(),
        EXPIRING_SOON_CARD_WINDOW,
      ),
    [expirationSchedule],
  )

  const sendReminder = (referral: Referral) => {
    if (!referral.can_remind) return
    fetchAuthenticated(`referrals/${referral.id}/remind`, {
      method: 'PATCH',
    }).then(() => {
      setReminderModalOpen(true)
    })
  }

  const onReminderClose = () => {
    dispatch(referralsCollection.actions.fetch())
    setReminderModalOpen(false)
  }

  const loadMore = () => {
    console.log('loading more transactions', loadedTransactions)
    setLoadedTransactions((prev) => prev + 10)
  }

  useEffect(() => {
    parentProps.setRewardError(null)
    return () => {
      parentProps.setRewardError(null)
    }
  }, [])

  return {
    ...parentProps,
    reminderModalOpen,
    sendReminder,
    onReminderClose,
    loadedTransactions,
    loadMore,
    transactionToDisplay,
    setTransactionToDisplay,
    programNamesWithExternalPayouts,
    nextExpiration,
    expiringSoonSchedule,
  }
}
